import { useEffect, useMemo, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { listContactMessages } from '@/lib/api';
import { ADMIN_MESSAGES as T } from '@/constants/testIds';

const CATEGORY_STYLE = {
  urgent_booking: { label: 'Urgent booking', className: 'bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold))] border-[hsl(var(--lxra-gold))]/30' },
  press: { label: 'Press', className: 'bg-[hsl(205,45%,30%)]/25 text-[hsl(205,55%,70%)] border-[hsl(205,45%,40%)]/40' },
  general_inquiry: { label: 'General inquiry', className: 'bg-transparent text-[hsl(var(--lxra-muted))] border-[hsl(var(--lxra-border))]' },
  vendor_spam: { label: 'Vendor/spam', className: 'bg-[hsl(8,45%,30%)]/20 text-[hsl(8,50%,65%)] border-[hsl(8,45%,40%)]/35' },
};

export const AdminMessagesTab = () => {
  const [messages, setMessages] = useState(null);
  const [urgentOnly, setUrgentOnly] = useState(false);

  useEffect(() => {
    listContactMessages().then(setMessages).catch(() => setMessages([]));
  }, []);

  const urgentCount = useMemo(() => (messages || []).filter((m) => m.category === 'urgent_booking').length, [messages]);
  const visible = useMemo(() => (urgentOnly ? (messages || []).filter((m) => m.category === 'urgent_booking') : (messages || [])), [messages, urgentOnly]);

  if (!messages) return <Skeleton className="h-64 rounded-2xl bg-[#141416]" />;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-[hsl(var(--lxra-muted))]">
          Submissions from the public contact form, auto-triaged by AI so urgent booking inquiries never get buried.
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-3 py-2">
          <ShieldAlert className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" />
          <Label htmlFor="urgent-only-switch" className="text-xs text-[hsl(var(--lxra-fg))] cursor-pointer select-none">
            Urgent only
          </Label>
          <Switch
            id="urgent-only-switch"
            checked={urgentOnly}
            onCheckedChange={setUrgentOnly}
            data-testid={T.urgentFilterToggle}
            className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]"
          />
          {urgentCount > 0 && (
            <span data-testid={T.urgentCount} className="inline-flex h-5 items-center rounded-full bg-[hsl(var(--lxra-gold))] px-2 text-[10px] font-bold text-[#0A0A0A]">
              {urgentCount}
            </span>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] overflow-hidden" data-testid={T.root}>
        <Table>
          <TableHeader>
            <TableRow className="border-[hsl(var(--lxra-border))] hover:bg-transparent">
              <TableHead className="text-[hsl(var(--lxra-muted))]">Priority</TableHead>
              <TableHead className="text-[hsl(var(--lxra-muted))]">Name</TableHead>
              <TableHead className="text-[hsl(var(--lxra-muted))]">Email</TableHead>
              <TableHead className="text-[hsl(var(--lxra-muted))]">Subject</TableHead>
              <TableHead className="text-[hsl(var(--lxra-muted))]">Message</TableHead>
              <TableHead className="text-[hsl(var(--lxra-muted))]">Received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow className="border-[hsl(var(--lxra-border))]">
                <TableCell colSpan={6} data-testid={T.empty} className="text-center text-[hsl(var(--lxra-muted))] py-10">
                  {urgentOnly ? 'No urgent bookings right now — nice and calm.' : 'No messages yet.'}
                </TableCell>
              </TableRow>
            )}
            {visible.map((m) => {
              const cat = CATEGORY_STYLE[m.category];
              return (
                <TableRow key={m.id} data-testid={T.row(m.id)} className="border-[hsl(var(--lxra-border))] hover:bg-[#141416]">
                  <TableCell>
                    {cat ? (
                      <Badge data-testid={T.category(m.id)} className={`border hover:bg-transparent text-[10px] whitespace-nowrap ${cat.className}`}>{cat.label}</Badge>
                    ) : (
                      <span className="text-[10px] text-[hsl(var(--lxra-muted))]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))] whitespace-nowrap">{m.name}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))] whitespace-nowrap">{m.email}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-fg))]">{m.subject}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] max-w-xs truncate">{m.message}</TableCell>
                  <TableCell className="text-[hsl(var(--lxra-muted))] whitespace-nowrap text-xs">
                    {new Date(m.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
